/**
 * V1.5.8.48 — SEO meta injector for public pages. Extended V2.0.50
 * (Feb 26, 2026) to accept direct override props so pages that don't
 * have a dedicated meta endpoint (e.g. per-recruiter DaddyTalk chat
 * pages, share landings) can pass values inline. Endpoint values win
 * over direct overrides so a background fetch can still refine the
 * card if a server-side endpoint is added later.
 *
 * V2.0.51 (Feb 26, 2026) — strips shell-injected OG duplicates on
 * mount. Rationale: the shell `public/index.html` carries site-wide
 * default og:* / twitter:* tags marked with `data-rh="true"` so
 * preview URLs (no crawler-cache) still render a proper NaukriDaddy
 * card on Facebook / LinkedIn / WhatsApp. But react-helmet-async
 * v3 does NOT actually claim those tags — it appends its per-page
 * tags alongside them, leaving duplicates that Facebook's OG parser
 * resolves to the first-seen (shell/site-wide) value. This effect
 * removes the shell defaults BEFORE Helmet renders so bots receiving
 * the crawler-cache-rendered HTML on production see only per-page
 * tags. On preview, the SPA still runs — but since no crawler is
 * scraping preview URLs (only humans + our own live-scraper), the
 * shell floor remains intact for those consumers via the raw HTML.
 *
 * Usage:
 *   <SeoMeta endpoint="/seo/job/{id}/meta" />
 *   <SeoMeta title="…" description="…" canonical="/some/path" />
 *   <SeoMeta endpoint="…" fallback={{ title: '…' }} />
 *
 * Renders nothing visible. Safe to drop into any public page.
 */
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";


// V2.0.51 — the OG/twitter properties we manage per-page. Any shell
// tag matching one of these gets removed on SeoMeta mount so Helmet's
// per-page injection is the only one present in the final DOM.
const MANAGED_OG_PROPERTIES = [
    "og:title",
    "og:description",
    "og:image",
    "og:image:width",
    "og:image:height",
    "og:image:type",
    "og:url",
];
const MANAGED_TWITTER_NAMES = [
    "twitter:title",
    "twitter:description",
    "twitter:image",
];


export default function SeoMeta({
    endpoint,
    fallback = {},
    // V2.0.50 — direct-override props. Merged UNDER endpoint data so a
    // server response can still override; if no endpoint is given they
    // become the primary source of truth.
    title,
    description,
    canonical,
    ogImage,
    ogType,
    twitterCard,
    robots,
    jsonLd,
}) {
    // Coalesce direct overrides into the initial meta so tags render on
    // first paint (crucial for crawler-cache which snapshots quickly).
    const overrides = React.useMemo(() => {
        const o = {};
        if (title !== undefined) o.title = title;
        if (description !== undefined) o.description = description;
        if (canonical !== undefined) {
            // Absolute canonical if a bare path was passed.
            o.canonical_url = canonical.startsWith("http")
                ? canonical
                : (typeof window !== "undefined" ? window.location.origin : "") + canonical;
        }
        if (ogImage !== undefined) o.og_image_url = ogImage;
        if (ogType !== undefined) o.og_type = ogType;
        if (twitterCard !== undefined) o.twitter_card = twitterCard;
        if (robots !== undefined) o.robots = robots;
        if (jsonLd !== undefined) o.json_ld = jsonLd;
        return o;
    }, [title, description, canonical, ogImage, ogType, twitterCard, robots, jsonLd]);

    const [meta, setMeta] = useState({ ...fallback, ...overrides });

    // V2.0.51 — strip shell OG defaults on mount so Helmet's per-page
    // tags become the only ones in the DOM. Runs pre-paint via
    // useLayoutEffect so we never leave duplicates in the crawler-
    // cache-serialized HTML on production. Guarded by `data-rh` so
    // we don't accidentally remove a tag Helmet already owns from a
    // previous mount.
    useLayoutEffect(() => {
        if (typeof document === "undefined") return;
        const stripBy = (selector) => {
            document.head.querySelectorAll(selector).forEach((el) => {
                // Only remove tags flagged by the shell as replaceable.
                // Helmet-injected tags (added via createElement) never
                // carry this attribute.
                if (el.getAttribute("data-rh") === "true") {
                    el.parentNode?.removeChild(el);
                }
            });
        };
        for (const p of MANAGED_OG_PROPERTIES) {
            stripBy(`meta[property="${p}"]`);
        }
        for (const n of MANAGED_TWITTER_NAMES) {
            stripBy(`meta[name="${n}"]`);
        }
    }, []);

    useEffect(() => {
        // Refresh whenever direct overrides change even without an endpoint.
        setMeta((m) => ({ ...m, ...overrides }));
    }, [overrides]);

    useEffect(() => {
        let cancelled = false;
        if (!endpoint) return;
        (async () => {
            try {
                const r = await api.get(endpoint);
                if (!cancelled && r?.data) setMeta({ ...fallback, ...overrides, ...r.data });
            } catch {
                /* Silent — fallback + overrides stay in place */
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    const finalTitle = meta.title || "NaukriDaddy — Direct jobs, no fees";
    const finalDescription = meta.description || "India's transparent recruitment platform. Apply directly.";
    const finalCanonical = meta.canonical_url;
    // V2.0.47 (Feb 26, 2026) — always emit an og:image. The shell no
    // longer carries a per-page-varying default (that was fighting
    // Helmet's per-page tag on Facebook, causing every job share to
    // render the generic landing card). Falls back to the site-wide
    // og-default.png when the SEO endpoint returns no image.
    const finalOgImage = meta.og_image_url || "https://naukridaddy.com/og-default.png";
    const finalOgType = meta.og_type || "website";
    const finalTwitterCard = meta.twitter_card || "summary_large_image";
    const finalRobots = meta.robots || "index, follow";
    const finalJsonLd = meta.json_ld;

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <meta name="robots" content={finalRobots} />
            {finalCanonical && <link rel="canonical" href={finalCanonical} />}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:type" content={finalOgType} />
            {finalCanonical && <meta property="og:url" content={finalCanonical} />}
            <meta property="og:image" content={finalOgImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:site_name" content="NaukriDaddy" />
            <meta name="twitter:card" content={finalTwitterCard} />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalOgImage} />
            {finalJsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(finalJsonLd)}
                </script>
            )}
        </Helmet>
    );
}
